import React, { useState, useContext, useEffect } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction"; // needed for dayClick
import nlLocale from "@fullcalendar/core/locales/nl";
import enLocale from "@fullcalendar/core/locales/en-gb";
import deLocale from "@fullcalendar/core/locales/de";
import styled from "@emotion/styled";

import SchedulerContext from "../schedulerContext";
import { dateFormat, dayFormat } from "../../tools";
import getEvent from "../eventModel";
import Toolbar from "./Toolbar";
import CustomPopover from "./Components/CustomPopover";
import Edit from "./Edit";
import Fill from "./Fill";

import Event from "./Event";
import DayCell from "./DayCell";
import WeekNumber from "./WeekNumber";
import QuickTools from "./QuickTools";
import useLockMutation from "../../useLockMutation";
import RouterPrompt from "../../Components/RouterPrompt";

const toolbarHeight = 8;
const initalView = "dayGridMonth"; // options: timeGridWeek or dayGridMonth

const StyleWrapper = styled.div`
  .fc .fc-daygrid-day-top {
    justify-content: center;
  }
  .fc .fc-daygrid-week-number {
    background-color: transparent;
  }
  .fc .fc-daygrid-day-number {
    padding: 6px;
    /*     font-size: 12px;
    font-weight: 500; */
  }
  .fc .fc-col-header-cell-cushion {
    text-transform: uppercase;
    color: #70757a;
    font-size: 12px;
    font-weight: 500;
  }
  .fc .fc-col-header-cell {
    border-bottom: ${(props) =>
      props.view === "dayGridMonth" ? "0px" : "1px solid #ddd"};
  }
  .fc .fc-timegrid-slot-minor {
    border: 0px;
  }
  .fc .fc-timegrid-event {
    border: 0;
    box-shadow: none;
  }
  .fc .fc-daygrid-day.fc-day-today {
    background-color: rgba(33, 150, 243, 0.08);
  }
  .fc .fc-timegrid-col.fc-day-today {
    background-color: rgba(33, 150, 243, 0.08);
  }
  .fc-popover {
    z-index: 1 !important;
  }
`;

const initialPopover = {
  open: false,
  type: "edit",
  eventId: "-1",
  event: getEvent({ type: "INITIAL_EVENT" }),
  endDay: moment().format(dateFormat),
  anchorId: "-1",
};
const initialRangeToCopy = { start: new Date(), endDay: new Date() };

export default function CustomCalendar({
  locked = true,
  variant = "calendar",
  startDay,
  Labels,
}) {
  const {
    events = [],
    eventsDispatch = () => false,
    interaction = { open: false },
    setInteraction = () => false,
    initEvents = [],
    saveMutation,
  } = useContext(SchedulerContext);
  const { t, i18n } = useTranslation();
  const getLocale = () => {
    switch (i18n.language) {
      case "nl":
        return nlLocale;
      case "de":
        return deLocale;
      default:
        return enLocale;
    }
  };
  const [isBlocking, setIsBlocking] = useState(false); // prevent navigation (e.g., unsaved calendar)
  const [updateIndex, setUpdateIndex] = useState(1);
  const calendarComponentRef = React.useRef(null);
  const calendarApi = calendarComponentRef?.current?.getApi();

  const [tempEvents, setTempEvents] = useState([]);
  const [rangeToCopy, setRangeToCopy] = useState(initialRangeToCopy);
  const [dayCellElements, setDayCellElements] = useState({}); // {["YYYY-MM-DD"]: position}
  const [eventElements, setEventElements] = useState({});
  const [view, setView] = useState(initalView);
  const [title, setTitle] = useState("");
  const [popover, setPopover] = useState(initialPopover); // custom dialog

  const handleCopy = () => setTempEvents(events); // make a copy of all events

  // constructor (at startup): set title of calendar
  useEffect(() => {
    if (calendarApi) {
      setTitle(calendarApi.view.title);
      if (startDay) {
        handleCalenderApi({ type: "gotoDate", payload: startDay }); // goto the startDay
        calendarApi.select(startDay);
      }
    }
  }, [calendarApi, startDay]);

  useEffect(() => {
    if (JSON.stringify(events) !== JSON.stringify(initEvents)) {
      setIsBlocking(true);
      setUpdateIndex((prev) => prev + 1);
    } else {
      setIsBlocking(false);
    }
  }, [events, initEvents]);

  // Lock the editor when editing questions
  const lockmutation = useLockMutation();
  useEffect(() => {
    // User have to make a change (updateIndex > 1) in the calendar
    if (updateIndex === 2) {
      lockmutation.mutate();
    }
    if (updateIndex > 2) {
      const timeOutId = setTimeout(() => {
        lockmutation.mutate();
      }, 60000);
      return () => {
        clearTimeout(timeOutId);
      };
    }
  }, [updateIndex]);

  useEffect(() => {
    if (interaction.open) {
      handleCopy();
      const date = moment().format(dayFormat);
      const eventToAdd = getEvent({
        type: "NEW_MONTH_EVENT",
        payload: { startDay: date, interaction: interaction },
      });
      handleCalenderApi({ type: "gotoDate", payload: date });
      eventsDispatch({ type: "ADD_EVENT", payload: eventToAdd });
      setPopover({
        open: true,
        type: "fill",
        eventId: eventToAdd.id,
        event: eventToAdd,
        anchorId: date,
      });
    }
  }, [interaction]);

  // control calendar views
  const handleCalenderApi = ({ type, payload }) => {
    if (!calendarApi) console.error("CalendarApi is not defined");
    switch (type) {
      case "goBack":
        calendarApi.prev();
        break;
      case "goNext":
        calendarApi.next();
        break;
      case "today":
        calendarApi.today();
        break;
      case "dayGridMonth":
        calendarApi.changeView("dayGridMonth");
        break;
      case "timeGridWeek":
        calendarApi.changeView("timeGridWeek");
        break;
      case "gotoDate":
        calendarApi.gotoDate(payload);
        break;
      case "gotoTime":
        calendarApi.scrollToTime(payload);
        break;
      case "save_calendar":
        saveMutation.mutate();
        break;
      default:
        break;
    }
    setView(calendarApi.view.type);
    setTitle(calendarApi.view.title);
  };

  const handlePopoverClose = () => {
    setPopover(initialPopover); // close the popover initiated by calendarClick
    setInteraction((prev) => ({ ...prev, open: false })); // close the popover initiated by buttonClick
  };

  const handlePopover = ({ type, payload }) => {
    switch (type) {
      case "add":
        eventsDispatch({ type: "ADD_EVENT", payload: popover.event });
        setPopover((prev) => ({
          ...prev,
          type: "fill",
        }));
        break;
      case "copy":
        setRangeToCopy({
          start: popover.event.start,
          endDay: popover.endDay,
        });
        handlePopoverClose();
        break;
      case "paste":
        eventsDispatch({
          type: "COPY_EVENTS",
          payload: {
            from: { start: rangeToCopy.start, endDay: rangeToCopy.endDay },
            to: {
              start: popover.event.start,
              endDay: popover.endDay,
            },
          },
        });
        handlePopoverClose();
        break;
      case "delete":
        eventsDispatch({
          type: "DELETE_RANGE",
          payload: { start: popover.event.start, endDay: popover.endDay },
        });
        handlePopoverClose();
        break;
      case "updateDayCellAnchorEl": // update popover position for daycell element
        setPopover((prev) => ({ ...prev, anchorId: payload }));
        break;
      case "save": // push
        eventsDispatch({ type: "UPDATE_EVENT", payload: payload });
        handlePopoverClose();
        break;
      case "cancel": // restore
        eventsDispatch({ type: "SET_EVENTS", payload: tempEvents });
        handlePopoverClose();
        break;
      case "delete_this": // delete only this event
        eventsDispatch({ type: "DELETE_THIS", payload: popover.event });
        handlePopoverClose();
        break;
      case "delete_day": // delete all event on same day
        eventsDispatch({ type: "DELETE_DAY", payload: popover.event });
        handlePopoverClose();
        break;
      case "delete_all": // delete all events
        eventsDispatch({ type: "DELETE_ALL", payload: popover.event });
        handlePopoverClose();
        break;
      default:
        break;
    }
  };

  const handleDateClick = (info) => {
    // TODO: show message: you can't schedule in the past
    const { date } = info;
    handleCopy();
    const source =
      info.jsEvent?.target.offsetParent?.value ??
      info.jsEvent?.target?.value ??
      undefined;
    switch (source) {
      case "day":
        // select day
        calendarApi.select(moment(date).format(dateFormat));
        break;

      case "week":
        // select week
        calendarApi.select(
          date,
          moment(date).add(7, "days").format(dateFormat)
        );
        break;

      case undefined:
        if (
          view === "timeGridWeek" &&
          moment(date).isSameOrAfter(moment(), "date") &&
          interaction.id !== "undefined"
        ) {
          const eventToAdd = getEvent({
            type: "NEW_WEEK_EVENT",
            payload: { startDay: date, interaction: interaction },
          });
          eventsDispatch({ type: "ADD_EVENT", payload: eventToAdd });
        }
        break;

      default:
        break;
    }
  };

  const handleSelection = (info) => {
    if (view === "dayGridMonth") {
      handleCopy();
      const { start, end } = info;
      const eventToAdd = getEvent({
        type: "NEW_MONTH_EVENT",
        payload: { startDay: start, interaction: interaction },
      });
      setPopover({
        open: true,
        type: "tools",
        eventId: eventToAdd.id,
        event: eventToAdd,
        endDay: moment(end, "YYYY-MM-DD").subtract(1, "day").format(dateFormat),
        anchorId: moment(start).format(dayFormat),
      }); // open the popover
    }
  };

  const handleEventClick = (arg) => {
    if (moment(arg.event.start).isBefore(moment(), "date")) return; // you can't edit passed events
    handleCopy();
    const eventToEdit = {
      ...arg.event.extendedProps,
      id: arg.event.id,
      start: moment(arg.event.start).format(dateFormat),
      end:
        arg.event.end === null
          ? null
          : moment(arg.event.end).format(dateFormat),
      title: arg.event.title,
    };
    setPopover({
      open: true,
      type: "edit",
      eventId: arg.event.id,
      event: eventToEdit,
      anchorId: arg.event.id,
    });
  };
  const handleEventChange = (arg) => {
    if (moment(arg.event.start).isBefore(moment(), "date")) return; // you can't change passed events
    const eventToUpdate = {
      id: arg.event.id,
      start: moment(arg.event.start).format(dateFormat),
      end:
        arg.event.end === null
          ? null
          : moment(arg.event.end).format(dateFormat),
    };
    eventsDispatch({ type: "UPDATE_EVENT", payload: eventToUpdate });
  };
  const calendarProps = locked
    ? {}
    : {
        dateClick: handleDateClick, // new beep
        eventClick: handleEventClick, // update control + focus
        eventChange: handleEventChange, // change day in dayGirdMonth
        eventDrop: handleEventChange,
        eventReceive: handleEventChange, // change hour in timeGridWeek
        select: handleSelection,
        editable: true,
        weekNumbers: true, // week numbers will be displayed on the side of each row of days in Month/DayGrid views
        weekNumberContent: (info) => <WeekNumber {...info} />,
      };
  const dayGridProps =
    variant === "protocol"
      ? { type: "basic", duration: { weeks: 2 }, fixedWeekCount: false }
      : null;

  return (
    <div style={{ width: "100%" }}>
      <RouterPrompt when={isBlocking} />
      {variant === "calendar" ? (
        <Toolbar
          variant="calendar"
          title={title}
          view={view}
          handleCalenderApi={handleCalenderApi}
          isBlocking={isBlocking}
        />
      ) : null}

      <StyleWrapper view={view}>
        <FullCalendar
          key={view} // refresh calendar when changing views
          {...calendarProps}
          ref={calendarComponentRef}
          initialView={view}
          locale={getLocale()}
          headerToolbar={false}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          events={events}
          //firstDay={new Date().getDay()} // returns the day number of the week, works!
          allDaySlot={false}
          eventContent={(eventInfo) => (
            <Event {...eventInfo} id={popover.eventId} locked={locked} />
          )}
          dayCellContent={(info) => <DayCell {...info} />}
          dayCellDidMount={(arg) => {
            // Daycell elements
            if (!arg.el.className.startsWith("fc-popover"))
              // In case element is a fc-popover, the daycell elemnt shouldn't update
              setDayCellElements((prev) => ({
                ...prev,
                [arg.el.dataset.date]: arg.el,
              }));
          }}
          eventDidMount={(arg) =>
            setEventElements((prev) => ({ ...prev, [arg.event.id]: arg.el }))
          }
          nowIndicator={true}
          height={variant === "protocol" ? "400px" : `${100 - toolbarHeight}vh`}
          dayMaxEvents={true} // allow "more" link when too many events
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            locale: nlLocale,
          }}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            locale: nlLocale,
          }}
          dayHeaderFormat={
            view === "dayGridMonth"
              ? { weekday: "short" }
              : {
                  weekday: "short",
                  //month: "numeric",
                  day: "numeric",
                  omitCommas: true,
                }
          }
          defaultTimedEventDuration={
            view === "dayGridMonth"
              ? { hours: 0, minutes: 0 }
              : { hours: 1, minutes: 0 }
          }
          views={{
            dayGrid: {
              // options apply to dayGridMonth, dayGridWeek, and dayGridDay views
              ...dayGridProps,
              selectable: true,
            },
            timeGrid: {
              slotEventOverlap: false,
              //slotLabelInterval: '00:45:00',
              //slotDuration: '00:15:00',
              selectable: false,
            }, // options apply to timeGridWeek and timeGridDay views
          }}
          eventConstraint={{
            start: moment().format("YYYY-MM-DD"),
            end: "2100-01-01", // hard coded goodness unfortunately
          }}
        />
      </StyleWrapper>
      {variant === "protocol" ? (
        <Toolbar
          variant="protocol"
          title={title}
          view={view}
          handleCalenderApi={handleCalenderApi}
          isBlocking={isBlocking}
          locked
          Labels={Labels}
        />
      ) : null}
      <CustomPopover
        dialog={popover}
        handlePopover={handlePopover}
        anchorEl={
          popover.type === "edit"
            ? eventElements[popover.anchorId] // click on event => edit interaction
            : dayCellElements[popover.anchorId] // click on day => fill calendar
        }
        content={() => {
          switch (popover.type) {
            case "edit": // edit an event
              return (
                <Edit initEvent={popover.event} handlePopover={handlePopover} />
              );
            case "fill": // add events
              return (
                <Fill
                  initEvent={popover.event}
                  endDay={popover.endDay}
                  tempEvents={tempEvents}
                  handlePopover={handlePopover}
                  handleCalenderApi={handleCalenderApi}
                />
              );
            default:
            case "tools": // copy, paste, delete calendar events
              return (
                <QuickTools
                  isEditable={
                    moment(popover.endDay).isSameOrAfter(moment(), "date") &&
                    interaction.id !== "undefined"
                  }
                  handlePopover={handlePopover}
                />
              );
          }
        }}
      />
    </div>
  );
}
